import { useEffect, useRef, useState } from 'react';

// A response belongs to one context. Abort older requests and refresh when the
// user returns to the app; a location change never exposes the previous data.
export function usePollingResult(key, url, intervalMs, empty) {
  const [result, setResult] = useState({ key: null, data: empty, loading: false });
  const emptyRef = useRef(empty);
  emptyRef.current = empty;
  useEffect(() => {
    if (!url) return;
    let stopped = false, controller;
    const refresh = async () => {
      controller?.abort();
      const current = controller = new AbortController();
      const timer = setTimeout(() => current.abort(), 20000);
      setResult(previous => ({ key, data: previous.key === key ? previous.data : emptyRef.current, loading: true }));
      try {
        const response = await fetch(url, { signal: current.signal });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const data = await response.json();
        if (!stopped && controller === current) setResult({ key, data, loading: false, error:null });
      } catch {
        if (!stopped && controller === current) setResult({ key, data: emptyRef.current, loading: false, error: 'Could not refresh observations' });
      } finally { clearTimeout(timer); }
    };
    const foreground = () => { if (!document.hidden) refresh(); };
    refresh();
    const interval = setInterval(refresh, intervalMs);
    document.addEventListener('visibilitychange', foreground);
    window.addEventListener('focus', foreground);
    return () => {
      stopped = true; controller?.abort(); clearInterval(interval);
      document.removeEventListener('visibilitychange', foreground);
      window.removeEventListener('focus', foreground);
    };
  }, [key, url, intervalMs]);
  return result.key === key && url ? result : { data: empty, loading: !!url };
}
